/**
 * apps/web :: lib/notifications-api
 *
 * Typed wrappers around the backend `/api/notifications/preferences` CRUD
 * landed in PR #75 (ADR-0008). Every call goes through {@link apiFetch} so
 * the auth cookie rides along and error handling stays consistent with the
 * rest of the SPA.
 *
 * Wire shape mirrors `NotificationPreferencesPayload` in the Ktor route —
 * keep the field names aligned with the backend `@Serializable` class; any
 * divergence lands here first so components consume a single stable type.
 */
import { ApiError, apiFetch } from "@/lib/api";

/** Channel identifiers accepted by `preferenceOrder` on the wire. */
export type NotificationChannelName =
  | "email"
  | "in_app_push"
  | "whatsapp"
  | "telegram"
  | "sms";

/**
 * Exact JSON shape of the preferences payload as exchanged with the backend.
 * Field ordering follows `NotificationPreferencesPayload.kt` for easy diffing.
 */
export interface NotificationPreferencesPayload {
  emailEnabled: boolean;
  inAppPushEnabled: boolean;
  whatsappEnabled: boolean;
  telegramEnabled: boolean;
  smsEnabled: boolean;
  marketingConsent: boolean;
  whatsappNumber: string | null;
  telegramChatId: string | null;
  smsNumber: string | null;
  preferenceOrder: NotificationChannelName[];
  consentGrantedAt: Record<string, string>;
}

/**
 * Client-side defaults used when the backend reports no row yet. Matches
 * `NotificationPreferences.default(userId)` on the Kotlin side so the initial
 * render of the settings form is identical to the first-save server echo.
 */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferencesPayload = {
  emailEnabled: true,
  inAppPushEnabled: true,
  whatsappEnabled: false,
  telegramEnabled: false,
  smsEnabled: false,
  marketingConsent: false,
  whatsappNumber: null,
  telegramChatId: null,
  smsNumber: null,
  preferenceOrder: ["email", "in_app_push"],
  consentGrantedAt: {},
};

const ENDPOINT = "/api/notifications/preferences";

/**
 * Fetch the current user's preferences. Returns server defaults if the
 * backend reports 404 (no row yet); the caller can render the form without
 * a separate empty-state branch.
 */
export async function getNotificationPreferences(): Promise<NotificationPreferencesPayload> {
  try {
    return await apiFetch<NotificationPreferencesPayload>(ENDPOINT);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { ...DEFAULT_NOTIFICATION_PREFERENCES };
    }
    throw err;
  }
}

/**
 * Upsert the full preferences payload. The backend performs its own
 * validation (channel-order names, etc.) and surfaces errors as 400s
 * with a `message` string — {@link ApiError} propagates that verbatim.
 */
export async function putNotificationPreferences(
  payload: NotificationPreferencesPayload,
): Promise<NotificationPreferencesPayload> {
  return await apiFetch<NotificationPreferencesPayload>(ENDPOINT, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

/**
 * GDPR erasure. Server replies 200 with `{ ok: true }`; we don't expose
 * that shape to callers because the only meaningful outcome is "the row
 * no longer exists" — the UI resets to defaults on success either way.
 */
export async function deleteNotificationPreferences(): Promise<void> {
  await apiFetch<{ ok: boolean }>(ENDPOINT, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Push subscription management (ADR-0008, VAPID web-push)
// ---------------------------------------------------------------------------

/** Wire shape of `GET /api/notifications/push/public-key`. */
export interface PushPublicKeyResponse {
  publicKey: string;
}

/**
 * Wire shape of each item in the `GET /api/notifications/push/subscribe`
 * list. Mirrors `PushSubscriptionView.kt` — and DELIBERATELY omits the
 * p256dh/auth key material, which is secret-at-rest on the server.
 */
export interface PushSubscriptionView {
  id: string;
  userAgent: string | null;
  deviceLabel: string | null;
  createdAtMs: number;
  lastUsedAtMs: number;
}

interface PushSubscriptionListResponse {
  subscriptions: PushSubscriptionView[];
}

/**
 * PUBLIC (no auth): fetch the VAPID public key so the browser can
 * construct a PushSubscription. 404 means this deploy has no VAPID
 * configured — surface as an {@link ApiError} and let the caller branch
 * into the "in-app push unavailable" UI.
 */
export async function getPushPublicKey(): Promise<PushPublicKeyResponse> {
  return await apiFetch<PushPublicKeyResponse>(
    "/api/notifications/push/public-key",
  );
}

/**
 * List the caller's active push subscriptions. Used by the Devices list UI
 * rendered beneath the InAppPush toggle.
 */
export async function listPushSubscriptions(): Promise<PushSubscriptionView[]> {
  const res = await apiFetch<PushSubscriptionListResponse>(
    "/api/notifications/push/subscribe",
  );
  return res.subscriptions;
}

/**
 * Remove a single device subscription. 404 from the server (id not
 * caller-owned) surfaces as {@link ApiError} — callers can refresh the
 * list on that to reconcile stale UI.
 */
export async function deletePushSubscription(id: string): Promise<void> {
  await apiFetch<{ ok: boolean; deleted: number }>(
    `/api/notifications/push/subscribe/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// Telegram Bot binding (ADR-0008)
// ---------------------------------------------------------------------------

/** Wire shape of `POST /api/notifications/telegram/init`. */
export interface TelegramInitResponse {
  /** 12-char alphanumeric verification code embedded in the deep link. */
  code: string;
  /** `https://t.me/<bot>?start=<code>` — clickable from the browser. */
  deepLink: string;
  /** Unix-millis when the code expires (default: 10 minutes after issue). */
  expiresAtMs: number;
}

/** Wire shape of `GET /api/notifications/telegram/status?code=<code>`. */
export interface TelegramStatusResponse {
  /** True once the bot has seen `/start <code>` and persisted the chat_id. */
  verified: boolean;
  /** Present (server echoes caller-owned data) when `verified` is true. */
  chatId?: number | null;
}

/**
 * Kick off a Telegram binding. The server generates a pairing code + the
 * `t.me/<bot>?start=...` deep link; the UI shows the link + polls
 * {@link getTelegramStatus} until it sees `verified: true`.
 */
export async function initTelegramBinding(): Promise<TelegramInitResponse> {
  return await apiFetch<TelegramInitResponse>(
    "/api/notifications/telegram/init",
    { method: "POST", body: "{}" },
  );
}

/**
 * Poll the verification status of a previously-issued code. Returns
 * `verified: true` once the backend has persisted the caller's
 * `chat_id` mapping. On `404` (unknown code) callers should treat the
 * binding as expired and re-init.
 */
export async function getTelegramStatus(
  code: string,
): Promise<TelegramStatusResponse> {
  return await apiFetch<TelegramStatusResponse>(
    `/api/notifications/telegram/status?code=${encodeURIComponent(code)}`,
  );
}

/**
 * Unbind. Removes the caller's `chat_id` mapping AND flips
 * `telegramEnabled` off in their notification preferences row.
 */
export async function deleteTelegramBinding(): Promise<void> {
  await apiFetch<{ ok: boolean; deleted: number }>(
    "/api/notifications/telegram/binding",
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// GDPR full-user erasure (DELETE /api/gdpr/me)
// ---------------------------------------------------------------------------

/**
 * Wire shape of `DELETE /api/gdpr/me`. Mirrors `ErasureResult.kt` in the
 * backend. Every boolean/count field is rendered verbatim so operators
 * can diagnose partial failures from the response payload without
 * consulting server logs.
 */
export interface ErasureResult {
  userId: string;
  notificationPreferencesDeleted: boolean;
  pushSubscriptionsDeleted: number;
  telegramBindingDeleted: boolean;
  sessionsInvalidated: number;
  workOsUserDeleted: boolean;
  /**
   * Count of `form_submissions` rows removed via the email_hash cascade
   * (ROADS-228). 0 when the user never filled a public form, or when email
   * resolution failed (in which case `partialFailure` includes
   * `form_submissions`).
   */
  formSubmissionsDeleted: number;
  /**
   * True iff a `user_profiles` row existed and was removed (ROADS-290).
   * Optional on the wire — older backend builds predating the profile
   * cascade simply omit it, defaulting to `undefined`, which the UI
   * treats as "no-op".
   */
  userProfileDeleted?: boolean;
  /**
   * Count of `practice_attempts` rows removed (ROADS-294). The backend
   * schema's FK cascade is on `question_id`, NOT `user_id`, so this step
   * runs explicitly and the count is authoritative. Optional on the wire
   * — older backend builds omit it and the UI treats as 0.
   */
  practiceAttemptsDeleted?: number;
  /** Null when every step succeeded; comma-separated step list otherwise. */
  partialFailure: string | null;
}

/**
 * Erase the authenticated user's full RoadBridge footprint (GDPR Art. 17).
 * Server wipes notification preferences, push subscriptions, telegram
 * bindings, sessions, and the upstream WorkOS user record. On success the
 * `rb_session` cookie is cleared server-side; callers should still flush
 * local auth state and navigate to `/login`.
 *
 * Throws {@link ApiError} on non-2xx. A 401 surfaces as an authentication
 * error and the caller should redirect to `/login` — there's nothing to
 * erase for an anonymous visitor.
 */
export async function deleteAccount(): Promise<ErasureResult> {
  return await apiFetch<ErasureResult>("/api/gdpr/me", { method: "DELETE" });
}
